import React, { useEffect, useState } from 'react';
import { healthsherpa } from './api';
import { History, personName, usePersonColors } from './HealthSherpaPerson';
import { usdCents } from './HealthSherpaPolicy';
import { METALS, carrierShortName, metalFill, policyColors } from './hsColors';
import { clientStatusBadge, displayAddress, displayName } from './hsLabels';
import { OwnerBox } from './HealthSherpaWork';   // 5.30.0
import { fs } from './hsScale';   // 5.31.0: font sizes scale with the page's Text size
import { ConsentPanel } from './HealthSherpaConsent';                 // 5.40.0
import PersonalInfo from './HsPersonalInfo';                            // 5.46.0

// =============================================================================
// ProfilePanel (5.22.0) — the HealthSherpa Clients tab's right-hand client profile (Henry 2026-10-12).
// Everything shown is ALREADY STORED in InsureDocs: the list row the user clicked, plus two reads of our own
// database (GET /healthsherpa/policy/people/{id} and its /history). Opening or switching the panel never calls
// HealthSherpa — there is deliberately no "Refresh now" here (it stays on the full record page).
// Sections: header (name, address, contact, ✕, Hide / Show list), current policy status with "last refreshed …
// CT", the policy details that used to be list columns, household members, another agent of record (if any),
// status history, and the actions. English only (the Clients tab has no language toggle).
// =============================================================================

const detailOf = (e) => e?.response?.data?.detail || e?.message || 'Something went wrong';
const REL = { primary: 'Primary', spouse: 'Spouse', dependent: 'Dependent' };
const memberName = (m) => [m.first, m.last].filter(Boolean).map(displayName).join(' ');

// One line: street, city ZIP (from the HealthSherpa export) — else the linked InsureDocs client's address line.
export function addressLine(r) {
  if (!r) return '';
  const cityZip = [displayAddress(r.city), r.zip].filter(Boolean).join(' ');
  const hs = [displayAddress(r.street || r.address), cityZip].filter(Boolean).join(', ');
  if (hs) return hs;
  return r.client_address ? displayAddress(r.client_address) : '';
}

function Section({ title, c, children, testid }) {
  return (
    <div style={{ marginTop: 14 }} data-testid={testid}>
      <div style={{ fontSize: fs(11), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: c.sub, marginBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}

function KV({ k, c, children }) {
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: fs(13), padding: '2px 0' }}>
      <div style={{ width: 120, flex: 'none', color: c.sub }}>{k}</div>
      <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</div>
    </div>
  );
}

function StatusChip({ r, darkMode }) {
  const st = clientStatusBadge(r, 'en');
  const t = policyColors(st.tone || 'neutral', darkMode);
  return (
    <span title={st.title || st.text} style={st.outlined
      ? { display: 'inline-block', padding: '1px 9px', borderRadius: 10, fontSize: fs(12), fontWeight: 600, border: `1px solid ${t.border}`, color: t.text }
      : { display: 'inline-block', padding: '1px 9px', borderRadius: 10, fontSize: fs(12), fontWeight: 600, border: `1px solid ${t.border}`, background: t.bg, color: t.text }}>
      {st.text}
    </span>
  );
}

function Metal({ metal, darkMode }) {
  if (!metal) return '—';
  const m = METALS[String(metal).toLowerCase().replace(/\s+/g, '_')];
  if (!m) return metal;
  const t = darkMode ? m.dark : m.light;
  return <span style={{ display: 'inline-block', padding: '0 8px', borderRadius: 4, fontSize: fs(12), fontWeight: 500, background: metalFill(t), color: t.text,
    border: `1px solid ${t.border || t.bg}` }}>{metal}</span>;
}

const btn = (c, primary) => ({
  padding: '6px 12px', fontSize: fs(13), borderRadius: 5, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: primary ? 600 : 400,
  border: primary ? '1px solid #185FA5' : `1px solid ${c.border}`, background: primary ? '#185FA5' : c.panel, color: primary ? '#fff' : c.text,
});

export default function ProfilePanel({ row, darkMode, isAdmin, listHidden, onToggleList, onClose, onOpenClient, onOpenFull, onAssign,
  onStartQuote, reloadKey = 0, onOpenWorkspace, onOwnerChanged }) {
  const c = usePersonColors(darkMode);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const pid = row.person_id;
  useEffect(() => {                         // our own database only (never HealthSherpa)
    let alive = true;
    setData(null); setError(null);
    healthsherpa.policy.person(pid).then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.status === 404 ? 'This person is not available.' : detailOf(e)); });
    return () => { alive = false; };
  }, [pid, reloadKey]);

  const p = data ? data.person : null;
  const addr = addressLine(row);
  const fromClient = addr && !(row.street || row.city || row.zip);
  const members = p ? (p.members || []) : [];
  // 5.28.1: only the backend's other_aor rule (aor.other / the other_aor alert) — never !aor.ours (unknown != other)
  const otherAor = (row.aor && row.aor.other === true) ? row.aor.label : null;
  const otherAorAlert = data ? (data.alerts || []).find((a) => a.key === 'other_aor') : null;
  const client = row.client || (p && p.client) || null;

  return (
    <div style={{ height: '100%', overflowY: 'auto', background: c.bg, color: c.text, padding: 16, boxSizing: 'border-box' }}
      data-testid="hs-profile" data-pid={pid} role="region" aria-label="Client profile">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: fs(19), fontWeight: 700 }} data-testid="hs-profile-name">{personName(row)}</div>
          <div style={{ fontSize: fs(13), color: c.sub, marginTop: 2 }}>
            {addr || 'No address on file'}{fromClient ? ' (InsureDocs client address)' : ''}
          </div>
          {p && (p.phone || p.email) && (
            <div style={{ fontSize: fs(13), color: c.sub, marginTop: 2 }}>{[p.phone, p.email].filter(Boolean).join(' · ')}</div>
          )}
        </div>
        <button style={btn(c)} onClick={onToggleList} data-testid="hs-profile-togglelist"
          title={listHidden ? 'Show the client list again' : 'Hide the client list (only this client stays on screen)'}>
          {listHidden ? 'Show list ▶' : '◀ Hide list'}
        </button>
        <button style={{ ...btn(c), padding: '6px 10px' }} onClick={onClose} aria-label="Close the profile (Esc)" title="Close (Esc)"
          data-testid="hs-profile-close">✕</button>
      </div>

      {/* 5.30.0: the claim — Claim / Change owner / Remove (anyone), and this client's workspace */}
      <Section title="Owner" c={c} testid="hs-profile-owner">
        <OwnerBox personId={pid} darkMode={darkMode} onChanged={onOwnerChanged} testid="hs-profile-ownerbox" />
        {onOpenWorkspace && (
          <button style={{ ...btn(c), marginTop: 8 }} onClick={() => onOpenWorkspace(pid)} data-testid="hs-profile-open-workspace">
            Open workspace (notes · to-do · follow-ups)
          </button>
        )}
      </Section>

      {/* 5.40.0: CMS consent — state, Send / Resend, Revoke (admins + managers), history + vault PDFs */}
      <Section title="CMS consent" c={c} testid="hs-profile-consent">
        <ConsentPanel darkMode={darkMode} personId={pid}
          initial={{ consumer_name: personName(row), phone: (p && p.phone) || '', email: (p && p.email) || '' }} />
      </Section>

      <Section title="Current policy status" c={c} testid="hs-profile-status">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <StatusChip r={row} darkMode={darkMode} />
          <span style={{ fontSize: fs(12), color: c.sub }} data-testid="hs-profile-refreshed">
            {row.last_refreshed_label ? `Last refreshed ${row.last_refreshed_label}` : 'Never refreshed from HealthSherpa'}
          </span>
        </div>
      </Section>

      <Section title="Policy" c={c} testid="hs-profile-policy">
        <KV k="Carrier" c={c}>{row.carrier ? (() => {
          const short = carrierShortName({ issuer_id: row.issuer_id, issuer_name: row.carrier });
          return short && short !== row.carrier ? `${short} — ${row.carrier}` : row.carrier;
        })() : '—'}</KV>
        <KV k="Plan" c={c}>{row.plan || '—'}</KV>
        <KV k="Metal" c={c}><Metal metal={row.metal} darkMode={darkMode} /></KV>
        <KV k="Premium (net)" c={c}>{row.net_premium_cents === null || row.net_premium_cents === undefined ? '—' : `${usdCents(row.net_premium_cents)} / month`}</KV>
        <KV k="City" c={c}>{row.city ? displayAddress(row.city) : '—'}</KV>
        <KV k="ZIP" c={c}>{row.zip || '—'}</KV>
        <KV k="Agent of record" c={c}>{row.aor ? (row.aor.ours === true ? `Ours — ${row.aor.label}` : row.aor.label) : '—'}</KV>
        <KV k="Last refreshed" c={c}>{row.last_refreshed_label || 'never'}</KV>
        <KV k="Docs" c={c}>{row.docs_needed ? 'Documents needed (DMI / SVI outstanding)' : 'None outstanding'}</KV>
        <KV k="Plan year" c={c}>{row.plan_year || '—'}</KV>
        <KV k="InsureDocs" c={c}>{client ? `${client.number ? `${client.number} · ` : ''}${displayName(client.name)}${client.restricted ? ' 🔒' : ''}` : 'Not assigned'}</KV>
      </Section>

      <Section title="Household" c={c} testid="hs-profile-household">
        {error && <div style={{ fontSize: fs(13), color: '#c0392b' }}>{error}</div>}
        {!data && !error && <div style={{ fontSize: fs(13), color: c.sub }}>Loading…</div>}
        {data && members.length === 0 && <div style={{ fontSize: fs(13), color: c.sub }}>—</div>}
        {members.map((m, i) => (
          <div key={`${m.relationship}-${i}`} style={{ fontSize: fs(13), padding: '1px 0' }}>
            {memberName(m)}<span style={{ color: c.sub }}> · {REL[m.relationship] || m.relationship}{m.applying === true ? ', applying' : m.applying === false ? ', not applying' : ''}</span>
          </div>
        ))}
      </Section>

      {/* 5.46.0: personal info for quoting — masked until Show (logged) */}
      <Section title="Personal info for quoting" c={c} testid="hs-profile-pi-section">
        <PersonalInfo personId={pid} darkMode={darkMode} testid="hs-profile-pi" compact />
      </Section>

      {(otherAor || otherAorAlert) && (
        <Section title="Another agent of record" c={c} testid="hs-profile-other-aor">
          <div style={{ fontSize: fs(13) }}>{otherAor || otherAorAlert.label}{otherAorAlert && otherAorAlert.since ? ` · since ${otherAorAlert.since}` : ''}</div>
        </Section>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
        {client && <button style={btn(c, true)} onClick={() => onOpenClient(client.id)} data-testid="hs-profile-open-client">Open InsureDocs client</button>}
        {onStartQuote && <button style={btn(c)} onClick={() => onStartQuote(client, pid)} data-testid="hs-profile-start-quote">Start quote</button>}
        <button style={btn(c)} onClick={onOpenFull} data-testid="hs-profile-open-full">Open full record</button>
        {!client && isAdmin && onAssign && <button style={btn(c)} onClick={() => onAssign(row)}>Assign</button>}
      </div>

      <History personId={pid} c={c} reloadKey={reloadKey} />
    </div>
  );
}
